import React from "react";

/* -------------------------------------------------------------------------- */
/* Shared UI primitives for PawCita. Tailwind-first, no lucide-react.         */
/* -------------------------------------------------------------------------- */

export const palette = {
  navy: "#0B2D4D",
  coral: "#FF5C5C",
  mint: "#67D5B5",
};

export function PawMark({ size = 22, className = "" }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 64 64"
      width={size}
      height={size}
      className={className}
      aria-hidden="true"
    >
      <g fill="currentColor">
        <ellipse cx="16" cy="22" rx="5" ry="7" />
        <ellipse cx="32" cy="14" rx="5" ry="7" />
        <ellipse cx="48" cy="22" rx="5" ry="7" />
        <ellipse cx="10" cy="38" rx="4.5" ry="6" />
        <ellipse cx="54" cy="38" rx="4.5" ry="6" />
        <path d="M32 28c-9 0-17 7-17 16 0 5 4 8 9 8 3 0 4-1 8-1s5 1 8 1c5 0 9-3 9-8 0-9-8-16-17-16z" />
      </g>
    </svg>
  );
}

export function Logo({ size = "md" }) {
  const sizes = {
    sm: "text-lg",
    md: "text-2xl",
    lg: "text-3xl",
  };

  return (
    <div
      className="inline-flex items-center gap-2"
      data-testid="pawcita-logo"
    >
      <img
        src="/pawcita-brand-icon-b6.png"
        alt=""
        aria-hidden="true"
        className="h-9 w-9 rounded-xl object-cover"
      />

      <span
        className={`font-display font-black ${sizes[size]}`}
        style={{ color: palette.navy }}
      >
        Paw<span style={{ color: palette.coral }}>Cita</span>
      </span>
    </div>
  );
}

const buttonVariants = {
  primary:
    "bg-[#FF5C5C] text-white shadow-[0_8px_24px_-10px_rgba(255,92,92,0.6)] hover:bg-[#ef4d4d] active:translate-y-[1px]",
  mint:
    "bg-[#67D5B5] text-[#0B2D4D] shadow-[0_8px_24px_-10px_rgba(103,213,181,0.7)] hover:bg-[#56c7a7] active:translate-y-[1px]",
  navy:
    "bg-[#0B2D4D] text-white hover:bg-[#153d63] active:translate-y-[1px]",
  outline:
    "border border-slate-200 bg-white text-[#0B2D4D] hover:bg-slate-50 active:translate-y-[1px]",
  ghost: "text-slate-600 hover:bg-slate-100 active:translate-y-[1px]",
  danger: "bg-rose-50 text-rose-700 hover:bg-rose-100 active:translate-y-[1px]",
};

export function Button({
  children,
  onClick,
  variant = "primary",
  className = "",
  type = "button",
  disabled = false,
  testId,
  ...rest
}) {
  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      data-testid={testId}
      className={`inline-flex items-center justify-center gap-2 rounded-full px-5 py-3 text-sm font-bold transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed ${buttonVariants[variant]} ${className}`}
      {...rest}
    >
      {children}
    </button>
  );
}

const chipTones = {
  mint: "bg-emerald-50 text-emerald-700 border-emerald-100",
  coral: "bg-rose-50 text-rose-700 border-rose-100",
  blue: "bg-sky-50 text-sky-700 border-sky-100",
  navy: "bg-[#0B2D4D]/5 text-[#0B2D4D] border-[#0B2D4D]/10",
  yellow: "bg-amber-50 text-amber-800 border-amber-100",
  gray: "bg-slate-50 text-slate-600 border-slate-100",
};

export function Chip({ children, tone = "mint", className = "", icon }) {
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-semibold ${chipTones[tone]} ${className}`}
    >
      {icon && <span aria-hidden="true">{icon}</span>}
      {children}
    </span>
  );
}

export function BackButton({ onClick, label = "Atrás", testId = "back-button" }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-white px-3 py-2 text-sm font-bold text-[#0B2D4D] shadow-sm transition hover:bg-slate-50 active:translate-y-[1px]"
      aria-label="Volver atrás"
    >
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M15 18l-6-6 6-6" />
      </svg>
      <span>{label}</span>
    </button>
  );
}

export function Input({
  label,
  error,
  className = "",
  testId,
  icon,
  ...props
}) {
  return (
    <label className={`block ${className}`}>
      {label && (
        <span className="mb-1.5 block text-sm font-semibold text-[#0B2D4D]">
          {label}
        </span>
      )}
      <div className="relative">
        {icon && (
          <span className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-slate-400">
            {icon}
          </span>
        )}
        <input
          data-testid={testId}
          {...props}
          className={`w-full rounded-2xl border bg-white px-4 py-3 text-sm outline-none transition focus:border-[#67D5B5] focus:ring-4 focus:ring-[#67D5B5]/15 ${
            icon ? "pl-11" : ""
          } ${error ? "border-rose-300" : "border-slate-200"}`}
        />
      </div>
      {error && (
        <span className="mt-1.5 block text-xs font-semibold text-rose-600">
          {error}
        </span>
      )}
    </label>
  );
}

export function Textarea({ label, className = "", testId, ...props }) {
  return (
    <label className={`block ${className}`}>
      {label && (
        <span className="mb-1.5 block text-sm font-semibold text-[#0B2D4D]">
          {label}
        </span>
      )}
      <textarea
        data-testid={testId}
        {...props}
        className="w-full resize-none rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm outline-none transition focus:border-[#67D5B5] focus:ring-4 focus:ring-[#67D5B5]/15"
      />
    </label>
  );
}

export function Select({ label, className = "", testId, children, ...props }) {
  return (
    <label className={`block ${className}`}>
      {label && (
        <span className="mb-1.5 block text-sm font-semibold text-[#0B2D4D]">
          {label}
        </span>
      )}
      <div className="relative">
        <select
          data-testid={testId}
          {...props}
          className="w-full appearance-none rounded-2xl border border-slate-200 bg-white px-4 py-3 pr-10 text-sm outline-none transition focus:border-[#67D5B5] focus:ring-4 focus:ring-[#67D5B5]/15"
        >
          {children}
        </select>
        <svg
          className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-slate-400"
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <path d="M6 9l6 6 6-6" />
        </svg>
      </div>
    </label>
  );
}

export function Card({ children, className = "", ...rest }) {
  return (
    <div
      {...rest}
      className={`rounded-[28px] border border-slate-100 bg-white shadow-[0_20px_50px_-30px_rgba(11,45,77,0.2)] ${className}`}
    >
      {children}
    </div>
  );
}

export function SectionTitle({ children, eyebrow, className = "" }) {
  return (
    <div className={className}>
      {eyebrow && (
        <p className="mb-2 text-xs font-black uppercase tracking-[0.18em] text-[#FF5C5C]">
          {eyebrow}
        </p>
      )}
      <h3 className="font-display text-2xl font-black text-[#0B2D4D] md:text-3xl">
        {children}
      </h3>
    </div>
  );
}

export function EmptyState({ emoji = "🐾", title, text, action }) {
  return (
    <div className="rounded-[28px] border border-dashed border-slate-200 bg-white/60 p-8 text-center">
      <div className="mx-auto mb-3 flex h-14 w-14 items-center justify-center rounded-2xl bg-emerald-50 text-2xl">
        {emoji}
      </div>
      <p className="font-display text-xl font-black text-[#0B2D4D]">{title}</p>
      {text && <p className="mx-auto mt-2 max-w-sm text-sm text-slate-500">{text}</p>}
      {action && <div className="mt-5">{action}</div>}
    </div>
  );
}

export function Toast({ message, tone = "navy", onClose }) {
  const bg = {
    navy: "bg-[#0B2D4D] text-white",
    coral: "bg-[#FF5C5C] text-white",
    mint: "bg-[#67D5B5] text-[#0B2D4D]",
  }[tone];
  return (
    <div
      role="status"
      data-testid="toast-message"
      className={`pc-pop fixed bottom-6 left-1/2 z-50 -translate-x-1/2 rounded-full px-5 py-3 text-sm font-bold shadow-xl ${bg}`}
      onAnimationEnd={() => {}}
    >
      <span>{message}</span>
      {onClose && (
        <button
          onClick={onClose}
          className="ml-3 opacity-70 transition hover:opacity-100"
          aria-label="Cerrar"
        >
          ✕
        </button>
      )}
    </div>
  );
}

/* Tiny decorative SVGs used across screens */
export function HeartIcon({ filled = false, size = 20, className = "" }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill={filled ? "currentColor" : "none"}
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden="true"
    >
      <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
    </svg>
  );
}

export function ShieldIcon({ size = 20, className = "" }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden="true">
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
      <path d="M9 12l2 2 4-4" />
    </svg>
  );
}

export function SendIcon({ size = 18, className = "" }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden="true">
      <path d="M22 2L11 13" />
      <path d="M22 2l-7 20-4-9-9-4 20-7z" />
    </svg>
  );
}

export function CalendarIcon({ size = 20, className = "" }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden="true">
      <rect x="3" y="4" width="18" height="18" rx="3" />
      <path d="M16 2v4M8 2v4M3 10h18" />
      <path d="M9 16l2 2 4-4" />
    </svg>
  );
}

export function MapPinIcon({ size = 16, className = "" }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden="true">
      <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
      <circle cx="12" cy="10" r="3" />
    </svg>
  );
}
